import { ReactElement, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Order } from '@/types';
import { X } from 'lucide-react';
import OrderDetailsTab from './OrderDetailsTab';
import OrderClientTab from './OrderClientTab';
import routes from '@/constants/routes';
import OrderTimer from './OrderTimer';

const INITIAL_TAB = 'details';

const OrderDetails = (order: Order) => {
  const navigate = useNavigate();
  const { organizationId } = useParams<{ organizationId: string }>();
  const [activeTab, setActiveTab] = useState<Tabs>(INITIAL_TAB);

  const tabs: Record<
    string,
    {
      label: string;
      component: ReactElement;
    }
  > = {
    details: {
      label: 'Detalles',
      component: <OrderDetailsTab {...order} />,
    },
    clientInfo: {
      label: 'Cliente',
      component: <OrderClientTab {...order} />,
    },
  };

  type Tabs = keyof typeof tabs;

  const handleChange = (_event: React.SyntheticEvent, newValue: Tabs) => {
    setActiveTab(newValue);
  };

  const handleClose = () => {
    navigate(
      routes.organizations.orders.main.replace(
        ':organizationId',
        organizationId ?? ''
      )
    );
  };

  return (
    <Drawer open={!!order.id} onClose={handleClose}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          padding: 2,
          paddingBottom: 0,
        }}
      >
        <Typography variant="h2">{`Pedido / ${order.number}`}</Typography>
        <Stack direction="row" alignItems="center " spacing={2}>
          <OrderTimer
            starts={order?.orderedAt ?? order?.createdAt}
            ends={order?.completedAt}
          />
          <Paper
            sx={{
              borderRadius: '50%',
            }}
          >
            <IconButton onClick={handleClose} color="primary">
              <X />
            </IconButton>
          </Paper>
        </Stack>
      </Stack>

      <Tabs value={activeTab} onChange={handleChange} sx={{ paddingX: 2 }}>
        {Object.entries(tabs).map(([tabKey, tab]) => (
          <Tab key={tabKey} label={tab.label} value={tabKey} />
        ))}
      </Tabs>
      <Divider />
      <Stack spacing={2} p={2} flex={1}>
        {tabs[activeTab].component}
      </Stack>
    </Drawer>
  );
};

export default OrderDetails;
